import * as THREE from 'three';
import type { GameState } from '../state/types';
import type { SceneContext } from '../scene/setup';

const DRAG_THRESHOLD_PX = 5;

export interface FieldInteractionHandlers {
  onHoverChange: (fieldId: number | null) => void;
  onFieldSelect: (fieldId: number) => void;
  onFieldDeselect: () => void;
}

function resolveFieldId(object: THREE.Object3D): number | null {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current.userData && typeof current.userData.fieldId === 'number') {
      return current.userData.fieldId;
    }
    current = current.parent;
  }
  return null;
}

export function bindFieldInteraction(
  ctx: SceneContext,
  state: GameState,
  handlers: FieldInteractionHandlers,
): void {
  const { container, scene, camera, renderer } = ctx;
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  let pointerDownX = 0;
  let pointerDownY = 0;
  let pointerDownOnMenu = false;

  const pickFieldId = (clientX: number, clientY: number): number | null => {
    if (!renderer) return null;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    for (const hit of intersects) {
      const fieldId = resolveFieldId(hit.object);
      if (fieldId !== null) return fieldId;
    }
    return null;
  };

  const isOnActionMenu = (target: EventTarget | null): boolean => {
    return target instanceof Element && !!target.closest('#field-action-menu');
  };

  container.addEventListener('pointermove', (event) => {
    if (!renderer) return;
    if (isOnActionMenu(event.target)) return;

    const fieldId = pickFieldId(event.clientX, event.clientY);
    if (fieldId !== state.hoveredFieldId) {
      state.hoveredFieldId = fieldId;
      handlers.onHoverChange(fieldId);
    }
  });

  container.addEventListener('pointerleave', () => {
    if (state.hoveredFieldId !== null) {
      state.hoveredFieldId = null;
      handlers.onHoverChange(null);
    }
  });

  container.addEventListener('pointerdown', (event) => {
    pointerDownX = event.clientX;
    pointerDownY = event.clientY;
    pointerDownOnMenu = isOnActionMenu(event.target);
  });

  container.addEventListener('click', (event) => {
    if (pointerDownOnMenu || isOnActionMenu(event.target)) return;

    const dx = event.clientX - pointerDownX;
    const dy = event.clientY - pointerDownY;
    if (dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      return;
    }

    if (!renderer) return;

    const clickedFieldId = pickFieldId(event.clientX, event.clientY);
    if (clickedFieldId !== null) {
      handlers.onFieldSelect(clickedFieldId);
    } else {
      handlers.onFieldDeselect();
    }
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      handlers.onFieldDeselect();
    }
  });
}
